/**
 * src/lib/agent/slashCommandRegistry.js
 *
 * Phase 82-04 — Chat System Unification.
 *
 * Registry of slash commands available in the unified composer. Each command
 * is either:
 *   - dispatch: 'tool'  — emits a tool-call on send (toolName required)
 *   - dispatch: 'local' — runs a local UI handler (localHandler slug)
 *
 * voiceSafe flag mirrors the voice-subset.js allowlist — only commands marked
 * voiceSafe may be executed during a Voice Mode session.
 *
 * requiresScope gates the command behind a scope entry of a given type
 * (e.g. /new-task requires a project chip).
 */

export const SLASH_LOCAL_HANDLERS = Object.freeze([
  'assign-project',
  'open-voice',
  'clear-messages',
]);

export const COMMANDS = Object.freeze([
  {
    slug: 'summarize',
    label: 'Summarize conversation',
    description: 'Recap the current thread in a few bullets',
    icon: 'FileText',
    dispatch: 'tool',
    toolName: 'summarize_conversation',
    voiceSafe: true,
    requiresScope: null,
  },
  {
    slug: 'new-task',
    label: 'New task',
    description: 'Create a task on the scoped project',
    icon: 'Plus',
    dispatch: 'tool',
    toolName: 'create_task',
    voiceSafe: true,
    requiresScope: 'project',
  },
  {
    slug: 'assign-project',
    label: 'Assign to project',
    description: 'Attach this chat to a project',
    icon: 'Briefcase',
    dispatch: 'local',
    localHandler: 'assign-project',
    voiceSafe: false,
    requiresScope: null,
  },
  {
    slug: 'search-brain',
    label: 'Search brain',
    description: 'Retrieve notes and context across projects',
    icon: 'Search',
    dispatch: 'tool',
    toolName: 'search_brain',
    voiceSafe: false,
    requiresScope: null,
  },
  {
    slug: 'voice',
    label: 'Voice mode',
    description: 'Open the full-screen voice overlay',
    icon: 'Mic',
    dispatch: 'local',
    localHandler: 'open-voice',
    voiceSafe: false,
    requiresScope: null,
  },
  {
    slug: 'clear',
    label: 'Clear messages',
    description: 'Reset the message list (keeps the chat)',
    icon: 'Eraser',
    dispatch: 'local',
    localHandler: 'clear-messages',
    voiceSafe: false,
    requiresScope: null,
  },
]);

const BY_SLUG = new Map(COMMANDS.map((c) => [c.slug, c]));

export function getCommand(slug) {
  return BY_SLUG.get(slug) || null;
}

export function validate(slug) {
  const cmd = getCommand(slug);
  if (!cmd) throw new Error(`Unknown slash command: /${slug}`);
  if (cmd.dispatch === 'tool' && !cmd.toolName) {
    throw new Error(`Slash command /${slug} is dispatch:tool but has no toolName`);
  }
  if (cmd.dispatch === 'local' && !SLASH_LOCAL_HANDLERS.includes(cmd.localHandler)) {
    throw new Error(`Slash command /${slug} has unknown localHandler: ${cmd.localHandler}`);
  }
  return cmd;
}

function scoreMatch(cmd, needle) {
  if (!needle) return 1;
  const slug = cmd.slug.toLowerCase();
  const label = (cmd.label || '').toLowerCase();
  if (slug === needle) return 100;
  if (slug.startsWith(needle)) return 80;
  if (label.startsWith(needle)) return 60;
  if (slug.includes(needle)) return 40;
  if (label.includes(needle)) return 30;
  let i = 0;
  for (const ch of slug) {
    if (ch === needle[i]) i += 1;
    if (i === needle.length) return 10;
  }
  return 0;
}

export function filter(query) {
  const needle = String(query || '').trim().toLowerCase().replace(/^\//, '');
  if (!needle) return [...COMMANDS];
  return COMMANDS
    .map((cmd) => ({ cmd, score: scoreMatch(cmd, needle) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.cmd.slug.localeCompare(b.cmd.slug))
    .map((x) => x.cmd);
}

export function isVoiceSafe(slug) {
  const cmd = getCommand(slug);
  return Boolean(cmd?.voiceSafe);
}

export function requiresScopeType(slug) {
  const cmd = getCommand(slug);
  return cmd?.requiresScope || null;
}

export default {
  COMMANDS,
  SLASH_LOCAL_HANDLERS,
  getCommand,
  validate,
  filter,
  isVoiceSafe,
  requiresScopeType,
};
